import { canUseMarketing } from './consent'

// Standard ChatGPT Ads events we report from the browser. "App Opened" is the
// custom event web_app_opened, sent by the pixel bootstrap in public/index.html.
type Event = 'registration_completed' | 'subscription_created' | 'trial_started'
type EventData =
  | { type: 'customer_action' }
  | { type: 'plan_enrollment'; plan_id?: string; amount?: number; currency?: string }
type PixelBridge = (event: Event, data: EventData, options: { event_id: string }) => boolean

const sent = new Set<string>()

function measure(event: Event, data: EventData, eventId: string): void {
  if (typeof window === 'undefined' || window.location.hostname !== 'design.bazaart.com' ||
    process.env.REACT_APP_IS_SANDBOX === 'true' || process.env.REACT_APP_IS_SANDBOX === '1' ||
    !canUseMarketing()) return
  const pixel = (window as Window & { bazaartChatGPTAdsMeasure?: PixelBridge }).bazaartChatGPTAdsMeasure
  const key = event + ':' + eventId
  if (!pixel || sent.has(key)) return
  try {
    if (pixel(event, data, { event_id: eventId })) sent.add(key)
  } catch {
    // A blocked measurement SDK must never interrupt auth or checkout completion.
  }
}

export function measureChatGPTRegistration(newUser: boolean, userId: unknown): void {
  if (!newUser || (typeof userId !== 'number' && typeof userId !== 'string') ||
    !String(userId).trim()) return
  measure('registration_completed', { type: 'customer_action' }, 'registration_' + String(userId))
}

function toMinorUnits(total: unknown, currency: unknown): { amount: number; currency: string } | null {
  if ((typeof total !== 'string' && typeof total !== 'number') ||
    !Number.isFinite(Number(total)) || typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) {
    return null
  }
  try {
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency })
      .resolvedOptions().maximumFractionDigits
    const amount = Math.round(Number(total) * Math.pow(10, digits))
    return Number.isSafeInteger(amount) && amount >= 0 ? { amount, currency } : null
  } catch {
    // Invalid or unsupported currency metadata is not measurable revenue.
    return null
  }
}

/**
 * Reports a completed Paddle Billing checkout.
 *
 * Reported after Paddle confirms checkout:
 * - Free trial starts (zero initial charge on a trial price) -> `trial_started`.
 * - First successful payment (positive initial charge) -> `subscription_created`
 *   AND `trial_started`, both with `amount`/`currency`.
 * - A zero charge without a trial, or bad metadata, is not reported.
 *
 * Deliberate deviation from OpenAI's taxonomy: `trial_started` doubles as
 * "any plan started" (trial or paid), because a campaign can optimize for only
 * one standard event and Bazaart optimizes ChatGPT Ads for plan starts.
 * Ads Manager's trial_started count therefore includes paid first purchases;
 * subscription_created stays paid-only.
 * The later trial-to-paid charge is not visible in the browser; it must be
 * sent as `subscription_created` server-side from the Paddle webhook.
 *
 * Paddle.js totals are major units; the pixel wants integer minor units.
 * Never pass recurring_totals (future billing). The transaction ID is the
 * pixel event_id so retries and duplicate callbacks are deduplicated.
 */
export function measureChatGPTCheckout(
  transactionId: unknown,
  total: unknown,
  currency: unknown,
  options: { trialStarted: boolean; planId?: unknown },
): void {
  if (typeof transactionId !== 'string' || !transactionId.trim()) return
  const money = toMinorUnits(total, currency)
  if (!money) return
  const planId = typeof options.planId === 'string' && options.planId.trim() ? options.planId : undefined

  const plan = planId ? { plan_id: planId } : {}

  if (money.amount > 0) {
    const paid = { type: 'plan_enrollment' as const, ...plan, amount: money.amount, currency: money.currency }
    measure('subscription_created', paid, transactionId)
    // Same event_id is fine: the pixel deduplicates per event name.
    measure('trial_started', paid, transactionId)
  } else if (options.trialStarted) {
    measure('trial_started', { type: 'plan_enrollment', ...plan }, transactionId)
  }
}
